import { writable, get } from 'svelte/store'
import { withCache } from '@lib/cache'
import type { SDMDeviceInfo } from '@types'
import { deviceLogStore } from '@lib/deviceLog'
import { authState } from '@lib/store/auth'
import { apiGet, apiPost } from '@api'

interface SDMConnection {
  serverUrl: string
  port: number
}

interface DeviceWSConnection {
  deviceId: string | null
  ws: WebSocket | null
  connected: boolean
  reconnecting: boolean
}

interface SDMApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: any
  headers?: Record<string, string>
  cache?: boolean
  cacheTTL?: number
}

export class SDMApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'SDMApiError'
  }
}

const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
const defaultProtocol = isLocalDev ? 'http' : 'https'
const defaultHost = isLocalDev ? 'localhost' : 'cli.sdm.samna.io'

const sdmConnection = writable<SDMConnection>({
  serverUrl: `${defaultProtocol}://${defaultHost}:8765`,
  port: 8765
})

const deviceWSConnection = writable<DeviceWSConnection>({
  deviceId: null,
  ws: null,
  connected: false,
  reconnecting: false
})

interface EnrollmentProgress {
  step: number
  totalSteps: number
  message: string
  error: string
  lastLog: string
}

interface EnrollmentState {
  deviceId: string | null
  enrolling: boolean
  progress: EnrollmentProgress
  commandOutput: string[]
  currentCommand: any | null
  selectedVersion: string
  config: any
  deviceToken: string
  tokenFetching: boolean
}

const initialEnrollmentState: EnrollmentState = {
  deviceId: null,
  enrolling: false,
  progress: { step: 0, totalSteps: 12, message: '', error: '', lastLog: '' },
  commandOutput: [],
  currentCommand: null,
  selectedVersion: '',
  config: {},
  deviceToken: '',
  tokenFetching: false
}

function createEnrollmentStore() {
  const storedState = typeof window !== 'undefined'
    ? localStorage.getItem('sdm-enrollment-state')
    : null

  const initialState = storedState
    ? { ...initialEnrollmentState, ...JSON.parse(storedState) }
    : initialEnrollmentState

  const { subscribe, set, update } = writable<EnrollmentState>(initialState)

  const persistState = (state: EnrollmentState) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('sdm-enrollment-state', JSON.stringify({
        enrolling: state.enrolling,
        progress: state.progress,
        deviceId: state.deviceId
      }))
    }
  }

  return {
    subscribe,
    set: (state: EnrollmentState) => {
      set(state)
      persistState(state)
    },
    update: (updater: (state: EnrollmentState) => EnrollmentState) => {
      update(state => {
        const newState = updater(state)
        persistState(newState)
        return newState
      })
    },
    reset: () => {
      set(initialEnrollmentState)
      if (typeof window !== 'undefined') {
        localStorage.removeItem('sdm-enrollment-state')
      }
    },
    setDevice: (deviceId: string) => update(state => ({
      ...state,
      deviceId,
      enrolling: false,
      progress: { step: 0, totalSteps: 12, message: '', error: '', lastLog: '' },
      commandOutput: [],
      currentCommand: null
    })),
    setProgress: (progress: Partial<EnrollmentProgress>) => update(state => ({
      ...state,
      progress: { ...state.progress, ...progress }
    })),
    addCommandOutput: (output: string) => update(state => ({
      ...state,
      commandOutput: [...state.commandOutput, output]
    })),
    clearCommandOutput: () => update(state => ({
      ...state,
      commandOutput: []
    })),
    setEnrolling: (enrolling: boolean) => update(state => ({ ...state, enrolling })),
    setCurrentCommand: (command: any) => update(state => ({ ...state, currentCommand: command })),
    setSelectedVersion: (version: string) => update(state => ({ ...state, selectedVersion: version })),
    setConfig: (config: any) => update(state => ({ ...state, config })),
    setDeviceToken: (deviceToken: string) => update(state => ({ ...state, deviceToken })),
    fetchToken: async () => {
      update(state => ({ ...state, tokenFetching: true }))

      try {
        const data = await apiGet('/sdm/token', {
          cache: true,
          cacheTTL: 30 * 60 * 1000
        })

        update(state => ({
          ...state,
          deviceToken: data.token,
          tokenFetching: false
        }))

        return data.token
      } catch (error) {
        update(state => ({ ...state, tokenFetching: false }))
        throw error
      }
    }
  }
}

export const enrollmentStore = createEnrollmentStore()

export function setSDMServer(params: URLSearchParams) {
  const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  const defaultHost = isLocalDev ? 'localhost' : 'cli.sdm.samna.io'
  const host = params.get('server') || defaultHost
  const port = parseInt(params.get('port') || '') || 8765
  const protocol = isLocalDev ? 'http' : 'https'
  const serverUrl = `${protocol}://${host}:${port}`
  sdmConnection.set({ serverUrl, port })
}

export function getSDMConnection() {
  return get(sdmConnection)
}

export const sdmConnectionStore = { subscribe: sdmConnection.subscribe }
export const deviceWSConnectionStore = { subscribe: deviceWSConnection.subscribe }

let reconnectAttempts = 0
let reconnectTimeoutId: NodeJS.Timeout | null = null
let shouldReconnect = true

export function connectDeviceWebSocket(deviceId: string, onMessage: (data: any) => void): void {
  const currentConnection = get(deviceWSConnection)

  if (currentConnection.ws) {
    console.log(`[WS] Already connected, closing previous connection`)
    shouldReconnect = false
    currentConnection.ws.close()
    shouldReconnect = true
  }

  const { serverUrl } = get(sdmConnection)
  const wsUrl = serverUrl.replace('http://', 'ws://').replace('https://', 'wss://')
  const ws = new WebSocket(`${wsUrl}/ws/device`)

  ws.onopen = () => {
    console.log(`[WS] Connected to CLI enrollment`)
    reconnectAttempts = 0
    if (reconnectTimeoutId) {
      clearTimeout(reconnectTimeoutId)
      reconnectTimeoutId = null
    }
    deviceWSConnection.set({
      deviceId,
      ws,
      connected: true,
      reconnecting: false
    })
  }

  ws.onmessage = (event) => {
    const data = JSON.parse(event.data)
    onMessage(data)
  }

  ws.onerror = (error) => {
    console.error(`[WS] Enrollment connection error:`, error)
  }

  ws.onclose = (event) => {
    console.log(`[WS] Disconnected from enrollment (code: ${event.code})`)
    deviceWSConnection.update(state => ({
      ...state,
      ws: null,
      connected: false,
      reconnecting: shouldReconnect
    }))

    if (shouldReconnect) {
      reconnectAttempts++
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempts - 1), 30000)
      console.log(`[WS] Reconnecting to enrollment in ${delay}ms (attempt ${reconnectAttempts})`)

      reconnectTimeoutId = setTimeout(() => {
        console.log(`[WS] Attempting reconnection to enrollment`)
        connectDeviceWebSocket(deviceId, onMessage)
      }, delay)
    } else {
      deviceWSConnection.set({
        deviceId: null,
        ws: null,
        connected: false,
        reconnecting: false
      })
    }
  }

  deviceWSConnection.set({
    deviceId,
    ws,
    connected: false,
    reconnecting: true
  })
}

export function disconnectDeviceWebSocket() {
  shouldReconnect = false

  if (reconnectTimeoutId) {
    clearTimeout(reconnectTimeoutId)
    reconnectTimeoutId = null
  }

  const currentConnection = get(deviceWSConnection)
  if (currentConnection.ws) {
    console.log(`[WS] Disconnecting from device ${currentConnection.deviceId}`)
    currentConnection.ws.close()
  }

  deviceWSConnection.set({
    deviceId: null,
    ws: null,
    connected: false,
    reconnecting: false
  })

  reconnectAttempts = 0
  shouldReconnect = true
}

function getServerUrl(): string {
  return get(sdmConnection).serverUrl
}

async function sdmRequest<T = any>(endpoint: string, options: SDMApiOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, cache = false, cacheTTL = 10 * 60 * 1000 } = options

  if (cache) {
    const cacheKey = `sdm:${method}:${endpoint}`
    return withCache(cacheKey, () => sdmRequestInternal<T>(endpoint, options), cacheTTL)
  }

  return sdmRequestInternal<T>(endpoint, options)
}

async function sdmRequestInternal<T = any>(endpoint: string, options: SDMApiOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options
  const url = `${getServerUrl()}${endpoint}`

  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers
    }
  }

  if (body && method !== 'GET') {
    config.body = JSON.stringify(body)
  }

  try {
    const response = await fetch(url, config)

    if (!response.ok) {
      throw new SDMApiError(response.status, `SDM API error: ${response.status} ${response.statusText}`)
    }

    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {} as T
    }

    return await response.json()
  } catch (err) {
    if (err instanceof SDMApiError) {
      throw err
    }
    throw new SDMApiError(0, `Network error: ${err instanceof Error ? err.message : 'Unknown error'}`)
  }
}

export async function sdmGet<T = any>(endpoint: string, options?: Omit<SDMApiOptions, 'method'>): Promise<T> {
  return sdmRequest<T>(endpoint, { method: 'GET', ...options })
}

export async function sdmPost<T = any>(endpoint: string, body?: any, options?: Omit<SDMApiOptions, 'method' | 'body'>): Promise<T> {
  return sdmRequest<T>(endpoint, { method: 'POST', body, ...options })
}

export async function sdmPut<T = any>(endpoint: string, body?: any, options?: Omit<SDMApiOptions, 'method' | 'body'>): Promise<T> {
  return sdmRequest<T>(endpoint, { method: 'PUT', body, ...options })
}

export async function sdmDelete<T = any>(endpoint: string, options?: Omit<SDMApiOptions, 'method'>): Promise<T> {
  return sdmRequest<T>(endpoint, { method: 'DELETE', ...options })
}

export const devices = () =>
  sdmGet<{ devices: SDMDeviceInfo[], count: number }>('/api/devices')

export const device = (deviceId: string) =>
  sdmGet<SDMDeviceInfo>(`/api/devices/${deviceId}`)

export const scan_lan = () =>
  sdmGet<{ servers: any[] }>('/api/scan-lan')

export const get_config = () =>
  sdmGet<Record<string, any>>('/api/config')

export const put_config = (config: Record<string, any>) =>
  sdmPut<{ status: string }>('/api/config', config)

export const generate_name = () =>
  sdmGet<{ name: string }>('/api/generate-name')

async function execute(label: string, totalSteps: number, fn: () => Promise<any>, stepNumber?: number) {
  enrollmentStore.clearCommandOutput()
  enrollmentStore.setEnrolling(true)
  enrollmentStore.setProgress({
    step: stepNumber || 0,
    totalSteps: stepNumber ? 11 : totalSteps,
    message: stepNumber ? label : 'Starting...',
    error: '',
    lastLog: label
  })
  enrollmentStore.addCommandOutput(`Starting ${label}...`)

  try {
    await fn()
    enrollmentStore.addCommandOutput('✓ Command sent to SDM CLI')
  } catch (e) {
    enrollmentStore.setEnrolling(false)
    enrollmentStore.setProgress({ error: `Failed to execute command: ${e}` })
    enrollmentStore.addCommandOutput(`✗ ERROR: Failed to execute command: ${e}`)
  }
}

async function sync() {
  const state = get(enrollmentStore)
  const config = state.config
  const auth = get(authState)

  try {
    const current = await get_config()

    const updated = {
      ...current,
      device_id: state.deviceId,
      adb_path: config.adbPath,
      apk_path: config.apkPath,
      package_name: config.packageName,
      admin_receiver: config.adminReceiver,
      launcher_activity: config.launcherActivity,
      device_activity: config.deviceActivity,
      device_admin_timeout: config.deviceAdminTimeout,
      boot_wait_timeout: config.bootWaitTimeout,
      auto_enroll: config.autoEnroll,
      use_latest: config.useLatest,
      device_name: config.deviceName,
      language: config.language,
      timezone: config.timezone,
      auto_wifi: config.autoWifi,
      log_app_only: config.logAppOnly,
      log_app: config.logApp,
      sdm_server_location: config.serverEndpoint,
      sdm_device_endpoint: config.deviceEndpoint,
      sdm_device_token: state.deviceToken,
      sdm_device_type: config.deviceType,
      sdm_registration_token: config.registrationToken,
      sdm_protocol: config.protocol,
      sdm_owner_id: auth.user?.organization_id || config.ownerId,
      network_configuration: {
        ssid: config.wifiSsid,
        pass: config.wifiPassword,
        security: current.network_configuration?.security || ''
      },
      device_configuration: {
        display: {
          brightness: config.brightness,
          autoRotate: config.autoRotate,
          screenTimeout: config.screenTimeout,
          darkMode: config.darkMode,
          navigationBarHidden: config.navigationBarHidden,
          statusBarHidden: config.statusBarHidden
        },
        network: {
          wifi: {
            ssid: config.wifiSsid,
            password: config.wifiPassword,
            enabled: config.wifiEnabled
          },
          bluetooth: config.bluetooth,
          mobileData: config.mobileData
        },
        security: {
          screenLock: {
            enabled: config.screenLockEnabled,
            type: config.screenLockType,
            value: config.screenLockValue
          }
        },
        system: {
          language: config.language,
          timezone: config.timezone,
          dateFormat: config.dateFormat,
          stayOnWhilePluggedIn: config.stayOnWhilePluggedIn,
          installNonMarketApps: config.installNonMarketApps
        },
        hardware: {
          gps: config.gps,
          camera: config.camera,
          microphone: config.microphone,
          speaker: config.speaker,
          usb: {
            mode: config.usbMode,
            dataTransfer: config.usbDataTransfer
          },
          locationMode: config.locationMode
        },
        policies: {
          restrictions: {
            allowScreenshot: config.allowScreenshot,
            allowClipboard: config.allowClipboard
          },
          compliance: {
            minimumOsVersion: config.minimumOSVersion
          },
          security: {
            disableADB: config.disableADB,
            disableDevelopment: config.disableDevelopment,
            disableKeyguard: config.disableKeyguard,
            disableStatusBar: config.disableStatusBar,
            blockUninstall: config.blockUninstall,
            disallowSafeBoot: config.disallowSafeBoot,
            disallowFactoryReset: config.disallowFactoryReset,
            disallowUSBFileTransfer: config.disallowUSBFileTransfer,
            autoGrantPermissions: config.autoGrantPermissions
          }
        },
        sdm: {
          sdm_log_window: config.sdmLogWindow,
          sdm_log_batch_size: config.sdmLogBatchSize,
          sdm_owner_name: config.sdmOwnerName,
          sdm_location: config.sdmLocation,
          sdm_building: config.sdmBuilding,
          sdm_floor: config.sdmFloor,
          sdm_room: config.sdmRoom,
          sdm_org_number: config.sdmOrgNumber,
          mqtt_retry_interval: config.mqttRetryInterval || 5,
          mqtt_retry_max: config.mqttRetryMax || 60
        }
      }
    }

    await put_config(updated)
  } catch (e) {
    console.error('Failed to sync config:', e)
  }
}

function sendWSCommand(command: string, params?: Record<string, any>): Promise<void> {
  const conn = get(deviceWSConnection)
  if (!conn.ws || !conn.connected) {
    throw new Error('WebSocket not connected')
  }

  return new Promise((resolve, reject) => {
    conn.ws!.send(JSON.stringify({ command, params: params || {} }))
    resolve()
  })
}

export async function enroll() {
  await sync()
  await execute('Full Enrollment', 14, () => sendWSCommand('enroll'))
}

export async function install_apk() {
  await execute('Install APK', 5, () => sendWSCommand('install-apk'), 2)
}

export async function set_owner() {
  await execute('Set Device Owner', 5, () => sendWSCommand('set-device-owner'), 4)
}

export async function setup_launcher() {
  await execute('Setup Launcher', 5, () => sendWSCommand('setup-launcher'), 5)
}

export async function configure_device() {
  await execute('Configure Device', 5, () => sendWSCommand('configure-device'), 6)
}

export async function configure_network() {
  await execute('Configure Network', 5, () => sendWSCommand('configure-network'), 7)
}

export async function set_token() {
  await execute('Set Device Token', 5, () => sendWSCommand('set-device-token'), 9)
}

export async function register() {
  await execute('Register Device', 5, () => sendWSCommand('register-device'), 10)
}

export async function reboot() {
  await execute('Reboot Device', 5, () => sendWSCommand('reboot-device'), 8)
}

export async function uninstall(force: boolean = false) {
  const state = get(enrollmentStore)
  await execute('Uninstall App', 5, () => sendWSCommand('uninstall', {
    device_id: state.deviceId,
    force
  }))
}

export async function disown() {
  const state = get(enrollmentStore)
  await execute('Remove Device Owner', 5, () => sendWSCommand('disown', {
    device_id: state.deviceId
  }))
}

export async function completeCLIAuth(accessToken: string, refreshToken: string): Promise<void> {
  await sdmPost('/callback', {
    sdm_access_token: accessToken,
    sdm_refresh_token: refreshToken
  })
}
